/**
 * KPICard — at-a-glance metric tile.
 * Mobile-first: grows to full width on phone; gradient accent; tabular numbers.
 */
import { Card, Typography } from 'antd'

const { Text } = Typography

export interface KPICardProps {
  label: string
  value: number | string
  prefix?: string
  suffix?: string
  precision?: number
  icon?: React.ReactNode
  color?: 'green' | 'red' | 'blue' | 'gold' | 'purple' | 'gray'
  trend?: { delta: number; label?: string }   // optional small trend
  hint?: string                                // sub-label (small)
  loading?: boolean
}

const COLOR_MAP = {
  green:  { bg: 'linear-gradient(135deg,#f6ffed 0%,#fff 100%)', accent: '#52c41a', icon: '#52c41a' },
  red:    { bg: 'linear-gradient(135deg,#fff2f0 0%,#fff 100%)', accent: '#ff4d4f', icon: '#ff4d4f' },
  blue:   { bg: 'linear-gradient(135deg,#e6f4ff 0%,#fff 100%)', accent: '#1677ff', icon: '#1677ff' },
  gold:   { bg: 'linear-gradient(135deg,#fffbe6 0%,#fff 100%)', accent: '#faad14', icon: '#faad14' },
  purple: { bg: 'linear-gradient(135deg,#f9f0ff 0%,#fff 100%)', accent: '#722ed1', icon: '#722ed1' },
  gray:   { bg: 'linear-gradient(135deg,#f5f5f5 0%,#fff 100%)', accent: '#8c8c8c', icon: '#8c8c8c' },
}

function fmtNumber(v: number | string, precision = 0) {
  const n = typeof v === 'string' ? Number(v) : v
  if (isNaN(n)) return String(v)
  return n.toLocaleString('en-IN', { minimumFractionDigits: precision, maximumFractionDigits: precision })
}

export default function KPICard({
  label, value, prefix, suffix, precision = 0,
  icon, color = 'blue', trend, hint, loading,
}: KPICardProps) {
  const c = COLOR_MAP[color]
  return (
    <Card
      loading={loading}
      variant="borderless"
      style={{
        background: c.bg,
        borderLeft: `4px solid ${c.accent}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        height: '100%',
      }}
      styles={{ body: { padding: 14 } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Text style={{ fontSize: 12, color: '#595959', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {label}
        </Text>
        {icon && (
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: 'rgba(255,255,255,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: c.icon, fontSize: 14,
            flexShrink: 0,
          }}>
            {icon}
          </div>
        )}
      </div>
      <div style={{
        marginTop: 8,
        fontSize: 24,
        fontWeight: 700,
        color: c.accent,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.2,
        wordBreak: 'break-word',
      }}>
        {prefix}{typeof value === 'number' ? fmtNumber(value, precision) : value}{suffix}
      </div>
      {(trend || hint) && (
        <div style={{ marginTop: 4, fontSize: 11, color: '#8c8c8c', display: 'flex', gap: 6 }}>
          {trend && (
            <span style={{ color: trend.delta >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
              {trend.delta >= 0 ? '↑' : '↓'} {Math.abs(trend.delta).toFixed(1)}%
            </span>
          )}
          {hint && <span>{hint}</span>}
        </div>
      )}
    </Card>
  )
}
